import { sortedLevels } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type {
  BuildingAreaKAM,
  BuildingAreaKAMOptions,
  BuildingAreaKeyMeasure,
  BuildingOptionKAM,
  BuildingOptionKAMOptions,
  BuildingPresetKAM,
  BuildingPresetKAMOptions,
  ProcureBuildingOption,
  ProcureBuildingPreset,
} from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import { camelCaseToTitleCase, snakeCaseToTitleCase } from "common/utils/strings";
import { getBuildingFloorAreaTakeoffAreas } from "doc/getBuildingFloorAreaTakeoffAreas";
import { optionSetStateSetFromBuilding } from "doc/options/optionSetStateTools";
import { formatOptionPrice } from "doc/options/optionSetTools";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import type { DataTablePricingContext } from "lib/options/calculatePricing";

type ProcureBuildingAreas = {
  readonly conditionedAreas: BuildingAreaKeyMeasure[];
  readonly unconditionedArea: number;
  readonly garageArea: number;
  readonly frontPorchArea: number;
  readonly rearPorchArea: number;
  readonly coveredPatioArea: number;
  readonly uncoveredPatioArea: number;
};

/** @deprecated - use the Building Query API instead */
function getBuildingAreas({ floorAreaTakeoffs, levels, roomsByLevel, rooms }: Building): ProcureBuildingAreas {
  // here we pull out the areas we want to use for procure
  const conditionedAreas: BuildingAreaKeyMeasure[] = [
    {
      name: "conditionedArea",
      category: "AREA",
      area: floorAreaTakeoffs[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA]
        ? floorAreaTakeoffs[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA].totalFloorArea
        : 0,
      handles: Object.values(rooms)
        .filter((room) => room.attributes.roomEnclosure === RoomEnclosure.CONDITIONED)
        .map(buildingEntityPickHandle),
    },
  ];

  const sorted = sortedLevels(levels);
  if (sorted.length > 1) {
    for (const level of sorted) {
      if (roomsByLevel[level.id].length === 0) {
        continue;
      }

      conditionedAreas.push({
        category: "AREA",
        name: `conditionedArea - ${level.name}`,
        area: floorAreaTakeoffs[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA]
          ? floorAreaTakeoffs[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA].levelFloorAreas[level.id].floorArea
          : 0,
        handles: roomsByLevel[level.id]
          .map((id) => rooms[id])
          .filter((room) => room.attributes.roomEnclosure === RoomEnclosure.CONDITIONED)
          .map(buildingEntityPickHandle),
      });
    }
  }

  const areas = {
    conditionedAreas,
    ...getBuildingFloorAreaTakeoffAreas({ floorAreaTakeoffs }),
  };

  return areas;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/** @deprecated - use the Building Query API instead */
function getBuildingOptions(building: Building, pricingContext?: DataTablePricingContext): ProcureBuildingOption[] {
  const { optionChoices } = building;
  const optionSetStates = optionSetStateSetFromBuilding(building, { includeNoDecisionSets: false });
  const results: ProcureBuildingOption[] = [];

  /** Match title-block aggregates only when Config pricing applies; schedule rows still list all selections otherwise. */
  const useConfigActiveOptionPrices =
    Boolean(pricingContext?.activeOptions) &&
    (pricingContext?.totalPriceOverride === null || pricingContext?.totalPriceOverride === undefined);

  Object.entries(optionChoices).forEach(([optionId, optionChoice]) => {
    const optionSet = optionSetStates[optionId]?.optionSet;
    if (!optionSet || optionChoice === undefined || optionChoice === null || !optionSet.options?.[optionChoice]) {
      return;
    }
    const option = optionSet.options[optionChoice];
    const activeOption = pricingContext?.activeOptions?.[optionId];

    let price: string | undefined;
    if (option.priceTbd) {
      price = "TBD";
    } else if (useConfigActiveOptionPrices && activeOption) {
      const numericPrice =
        pricingContext?.includeElevationsInBaseHomePrice && optionId === "elevationStyle" ? 0 : activeOption.price;
      price = currencyFormatter.format(numericPrice);
    } else {
      price = formatOptionPrice(option, optionChoices);
    }

    const procureOption: ProcureBuildingOption = {
      optionKey: `${optionId}.${optionChoice}`,
      category: optionSet.category,
      optionSetName: optionSet.name,
      optionSetDescription: optionSet.description,
      description: option.description,
      optionName: option.name,
      price,
      builderOptionCode: option.builderOptionCode ?? undefined,
      builderTemplateCode: option.builderTemplateCode ?? undefined,
      builderOptionSetCode: optionSet.builderOptionSetCode ?? undefined,
      salesCategory: optionSet.salesCategory ? snakeCaseToTitleCase(optionSet.salesCategory) : undefined,
      // future: add handles to optionSetState
      handles: [],
    };
    results.push(procureOption);
  });

  return results;
}

/** @deprecated - use the Building Query API instead */
function getBuildingPresets({ doc }: Building, buildingOptions: ProcureBuildingOption[]): ProcureBuildingPreset[] {
  const { presets, optionSets } = doc;
  const results: ProcureBuildingPreset[] = [];
  const optionKeys = buildingOptions.map((option) => {
    return option.optionKey;
  });

  Object.entries(presets).forEach(([presetId, preset]) => {
    const { optionChoices, type } = preset;
    if (type !== "PROCURE") {
      return;
    }

    const presetOptionIsDefault: string[] = [];
    const presetOptionKeys = Object.entries(optionChoices).map(([optionId, optionChoice]) => {
      const key = `${optionId}.${optionChoice}`;
      if (optionSets[optionId].defaultValue === optionChoice) {
        presetOptionIsDefault.push(key);
      }
      return key;
    });

    if (
      (presetOptionKeys.length || !optionKeys.length) &&
      presetOptionKeys.every((optionKey) => optionKeys.includes(optionKey) || presetOptionIsDefault.includes(optionKey))
    ) {
      const presetRes: ProcureBuildingPreset = {
        presetId,
        name: preset.name,
        price: preset.price ?? 0,
        builderOptionCode: preset.builderOptionCode ?? "",
        optionChoices: Object.entries(optionChoices)
          .map(([optionId, optionChoice], idx) => {
            const option = buildingOptions.find((option) => {
              return option.optionKey === `${optionId}.${optionChoice}`;
            });

            if (!option) {
              return undefined;
            }
            return `(${idx + 1}) ${option.optionSetName} - ${option.optionName}`;
          })
          .filter((x) => !!x)
          .join(", "),
        // future: add handles
        handles: [],
      };
      results.push(presetRes);
    }
  });

  return results;
}

export function makeBuildingAreaKAM(item: BuildingAreaKeyMeasure): BuildingAreaKAM {
  return {
    type: "BUILDING_AREA",
    attributes: {
      category: keyAttributeNew(item.category),
      name: keyAttributeNew(camelCaseToTitleCase(item.name)),
      entityName: keyAttributeNew(`${item.category}.${item.name}`),
    },
    measures: {
      area: keyMeasureNew(item.area, "SQ FT"),
      count: keyMeasureNew(1),
    },
    handles: [...item.handles],
  };
}
export const EMPTY_BUILDING_AREA_KAM: BuildingAreaKAM = {
  type: "BUILDING_AREA",
  attributes: {
    category: keyAttributeNew("AREA"),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultBuildingAreaKAMOptions(): BuildingAreaKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}
export const defaultBuildingAreaKAMSort = (a: BuildingAreaKAM, b: BuildingAreaKAM) => {
  return b.measures.area.value > a.measures.area.value ? 1 : -1;
};

/** @deprecated - use the Building Query API instead */
export function buildingAreaKAMs(building: Building): BuildingAreaKAM[] {
  const buildingAreaMeasures = getBuildingAreas(building);
  const buildingAreaKAMs: BuildingAreaKAM[] = [];
  Object.entries(buildingAreaMeasures).forEach(([name, value]) => {
    if (value === 0) {
      return;
    }

    if (name === "conditionedAreas") {
      buildingAreaKAMs.push(...(value as BuildingAreaKeyMeasure[]).map(makeBuildingAreaKAM));
      return;
    }

    buildingAreaKAMs.push(
      makeBuildingAreaKAM({ name, category: "AREA", area: value, handles: [] } as BuildingAreaKeyMeasure),
    );
  });

  return buildingAreaKAMs;
}

export function makeBuildingOptionKAM(item: ProcureBuildingOption): BuildingOptionKAM {
  return {
    type: "BUILDING_OPTION",
    attributes: {
      optionKey: keyAttributeNew(item.optionKey),
      optionSet: keyAttributeNew(item.optionSetName),
      description: keyAttributeNew(item.description ?? ""),
      optionSetDescription: keyAttributeNew(item.optionSetDescription ?? ""),
      optionCode: keyAttributeNew(item.builderOptionCode ?? ""),
      name: keyAttributeNew(camelCaseToTitleCase(item.optionName)),
      category: keyAttributeNew(item.category ?? ""),
      salesCategory: keyAttributeNew(item.salesCategory ?? ""),
      entityName: keyAttributeNew(item.optionKey),
      builderOptionSetCode: keyAttributeNew(item.builderOptionSetCode ?? ""),
      builderTemplateCode: keyAttributeNew(item.builderTemplateCode ?? ""),
      price: keyAttributeNew(String(item.price ?? 0)),
    },
    measures: {
      count: keyMeasureNew(1),
    },
    handles: [...item.handles],
  };
}
export const EMPTY_BUILDING_OPTION_KAM: BuildingOptionKAM = {
  type: "BUILDING_OPTION",
  attributes: {
    category: keyAttributeNew(""),
    salesCategory: keyAttributeNew(""),
    optionKey: keyAttributeNew(""),
    description: keyAttributeNew(""),
    optionCode: keyAttributeNew(""),
    optionSet: keyAttributeNew(""),
    optionSetDescription: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
    builderOptionSetCode: keyAttributeNew(""),
    builderTemplateCode: keyAttributeNew(""),
    price: keyAttributeNew(""),
  },
  measures: {
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultBuildingOptionKAMOptions(): BuildingOptionKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      count: keyMeasureOptionsNew({}),
    },
  };
}
export const defaultBuildingOptionKAMSort = (a: BuildingOptionKAM, b: BuildingOptionKAM) => {
  return a.attributes.name.value.localeCompare(b.attributes.name.value);
};

export function makeBuildingPresetKAM(item: ProcureBuildingPreset): BuildingPresetKAM {
  return {
    type: "BUILDING_PRESET",
    attributes: {
      presetId: keyAttributeNew(item.presetId),
      name: keyAttributeNew(camelCaseToTitleCase(item.name)),
      optionChoices: keyAttributeNew(item.optionChoices),
      builderOptionCode: keyAttributeNew(item.builderOptionCode),
      entityName: keyAttributeNew(item.presetId),
    },
    measures: {
      count: keyMeasureNew(1),
    },
    handles: [...item.handles],
  };
}
export const EMPTY_BUILDING_PRESET_KAM: BuildingPresetKAM = {
  type: "BUILDING_PRESET",
  attributes: {
    presetId: keyAttributeNew(""),
    name: keyAttributeNew(""),
    optionChoices: keyAttributeNew(""),
    builderOptionCode: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultBuildingPresetKAMOptions(): BuildingPresetKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      count: keyMeasureOptionsNew({}),
    },
  };
}
export const defaultBuildingPresetKAMSort = (a: BuildingPresetKAM, b: BuildingPresetKAM) => {
  return a.attributes.name.value.localeCompare(b.attributes.name.value);
};

/** @deprecated - use the Building Query API instead */
export function buildingOptionKAMs(
  building: Building,
  pricingContext?: DataTablePricingContext,
): { options: BuildingOptionKAM[]; presets: BuildingPresetKAM[] } {
  const buildingOptionMeasures = getBuildingOptions(building, pricingContext);
  const buildingPresetMeasures = getBuildingPresets(building, buildingOptionMeasures);

  return {
    options: buildingOptionMeasures.map(makeBuildingOptionKAM),
    presets: buildingPresetMeasures.map(makeBuildingPresetKAM),
  };
}
